import React, { forwardRef, useEffect, useRef } from 'react'

import { css } from 'src/ui/style/css'
import { useTheme } from 'src/ui/style/useTheme'
import { View } from 'src/ui/basic/View'
import { isMobileScreen, ScreenSize } from '../../style/ScreenSize'
import { useLocale } from 'src/i18n/locale'
import { MarkdownText } from './MarkdownText'

const { Sizes } = ScreenSize
export interface MessageMetaItem {
    hash?: string
    depth?: number
    value: string
    lang?: string
    originalTopicName?: string
}

export interface Message {
    id: string
    text: string
    sender: 'user' | 'bot'
    timestamp: Date
    metaItems?: MessageMetaItem[]
}

interface ChatMessagesProps {
    messages: Message[]
    loading?: boolean
    onMetaClick?: (item: MessageMetaItem) => void
}

const ChatMessages = forwardRef<HTMLDivElement, ChatMessagesProps>(
    ({ messages, loading = false, onMetaClick }, ref) => {
        const th = useTheme()
        const locale = useLocale()
        const messagesEndRef = useRef<HTMLDivElement>(null)
        const isMobile = isMobileScreen()

        useEffect(() => {
            const container =
                (ref as React.RefObject<HTMLDivElement>)?.current ??
                messagesEndRef.current?.parentElement
            if (!container) return

            const frame = requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight
            })
            return () => cancelAnimationFrame(frame)
        }, [messages.length, isMobile])

        const formatTime = (date: Date) => {
            return date.toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit'
            })
        }

        return (
            <div
                ref={ref}
                id="chat-messages"
                css={css`
                    flex: 1;
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior: contain;
                    max-height: 100svh;
                    overflow-x: hidden;
                    padding: 0.625rem;
                    background-color: ${th.colors.background};
                    display: flex;
                    flex-direction: column;
                    gap: 0.625rem;
                    justify-content: flex-start;

                    @media (max-width: ${Sizes.md}px) {
                        padding: 0.75rem 0.5rem 0;
                        margin-bottom: 4.375rem;
                        gap: 0.5rem;
                        padding-top: max(
                            calc(3.5rem + 0.75rem),
                            calc(var(--safe-area-top) + 3.5rem + 0.75rem)
                        );

                        /* Better Android scrolling */
                        scroll-behavior: auto;
                        -webkit-overflow-scrolling: touch;
                        overscroll-behavior: contain;

                        /* Ensure it takes available space */
                        min-height: 0;
                        flex-shrink: 1;
                    }

                    @media (orientation: landscape) and (max-height: ${Sizes.landscapeH}px) {
                        padding: 0.75rem 0.5rem 0;
                        margin-bottom: 4.375rem;
                        gap: 0.5rem;
                        padding-top: max(
                            calc(2.75rem + 0.625rem),
                            calc(var(--safe-area-top) + 2.75rem + 0.625rem)
                        );
                        scroll-behavior: auto;
                        -webkit-overflow-scrolling: touch;
                        overscroll-behavior: contain;
                        min-height: 0;
                        flex-shrink: 1;
                    }
                `}
            >
                {messages.map(message => (
                    <View
                        key={message.id}
                        css={css`
                            display: flex;
                            flex-direction: column;
                            align-self: ${message.sender === 'user'
                                ? 'flex-end'
                                : 'flex-start'};
                            max-width: 80%;

                            @media (max-width: ${Sizes.md}px) {
                                max-width: 85%;
                            }

                            @media (max-width: ${Sizes.sm}px) {
                                max-width: 90%;
                            }
                        `}
                    >
                        <View
                            css={css`
                                background-color: ${
                                    message.sender === 'user'
                                        ? th.colors.tertiary
                                        : th.colors.white
                                };
                                padding: 0.625rem 0.9375rem;
                                border-radius: 1.125rem;
                                border-top-${
                                    message.sender === 'user' ? 'right' : 'left'
                                }-radius: 0.25rem;
                                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                                word-break: break-word;
                                display: flex;
                                flex-direction: column;
                                @media (max-width: ${Sizes.md}px) {
                                    padding: 0.5rem 0.75rem;
                                    font-size: 1rem;
                                    line-height: 1.4;
                                }

                            `}
                        >
                            {/* {message.text} */}
                            <MarkdownText
                                content={message.text}
                                openLinksInNewTab
                                color={
                                    message.sender === 'user'
                                        ? th.colors.white
                                        : th.colors.black
                                }
                            />
                            <span
                                css={css`
                                    font-size: 0.75rem;
                                    color: ${
                                        message.sender === 'user'
                                            ? th.colors.white
                                            : th.colors.black
                                    };
                                    font-weight: ${th.fontWeight.bold};
                                    margin-top: 0.25rem;
                                    align-self: flex-start;

                                    @media (max-width: ${Sizes.md}px) {
                                        font-size: 0.6875rem;
                                        margin-top: 0.125rem;
                                    }
                                `}
                            >
                                {formatTime(message.timestamp)}
                            </span>
                        </View>
                        {message.sender === 'bot' &&
                            message.metaItems &&
                            message.metaItems.length > 0 && (
                                <View
                                    css={css`
                                        margin-top: 0.375rem;
                                        padding: 0.5rem;
                                        border-radius: 0.875rem;
                                        background-color: ${th.colors.whiteWithOpacity};
                                        border: 1px solid rgba(0, 0, 0, 0.06);
                                        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
                                        display: flex;
                                        flex-direction: column;
                                        gap: 0.375rem;
                                        width: 100%;

                                        @media (max-width: ${Sizes.md}px) {
                                            padding: 0.375rem;
                                            gap: 0.3125rem;
                                        }
                                    `}
                                >
                                    {message.metaItems.map((item, idx) => (
                                        <button
                                            key={`${message.id}-meta-${item.hash}-${idx}`}
                                            type="button"
                                            onClick={() => onMetaClick?.(item)}
                                            css={css`
                                                display: flex;
                                                align-items: flex-start;
                                                gap: 0.5rem;
                                                width: 100%;
                                                text-align: left;
                                                border-radius: 0.75rem;
                                                padding: 0.5rem 0.625rem;
                                                background-color: ${th.colors.white};
                                                border: 1px solid rgba(0, 0, 0, 0.08);
                                                color: ${th.colors.black};
                                                cursor: pointer;
                                                transition: all 0.2s ease;

                                                &:hover {
                                                    background-color: ${th.colors.lightBlue};
                                                    border-color: ${th.colors.primary};
                                                }

                                                &:active {
                                                    transform: translateY(1px);
                                                }

                                                &:focus-visible {
                                                    outline: 2px solid ${th.colors.primary};
                                                    outline-offset: 2px;
                                                }

                                                @media (max-width: ${Sizes.md}px) {
                                                    padding: 0.625rem 0.75rem;
                                                    font-size: 0.9375rem;
                                                }
                                            `}
                                        >
                                            <span
                                                css={css`
                                                    width: 1.25rem;
                                                    height: 1.25rem;
                                                    border-radius: 999px;
                                                    background-color: ${th.colors.tertiary};
                                                    color: ${th.colors.white};
                                                    font-size: 0.75rem;
                                                    font-weight: ${th.fontWeight.bold};
                                                    display: inline-flex;
                                                    align-items: center;
                                                    justify-content: center;
                                                    flex-shrink: 0;
                                                    margin-top: 0.125rem;
                                                `}
                                            >
                                                {idx + 1}
                                            </span>
                                            <span
                                                css={css`
                                                    flex: 1;
                                                    line-height: 1.35;
                                                    word-break: break-word;
                                                `}
                                            >
                                                {item.value}
                                            </span>
                                        </button>
                                    ))}
                                </View>
                            )}
                    </View>
                ))}
                {/* )} */}

                {loading && (
                    <View
                        css={css`
                            align-self: flex-start;
                            background-color: ${th.colors.white};
                            color: ${th.colors.black};
                            padding: 0.625rem 0.9375rem;
                            border-radius: 1.125rem;
                            border-bottom-left-radius: 0.25rem;
                            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                            margin-top: 0.625rem;

                            @media (max-width: ${Sizes.md}px) {
                                padding: 0.5rem 0.75rem;
                                margin-top: 0.5rem;
                            }
                        `}
                    >
                        <div
                            css={css`
                                display: flex;
                                gap: 0.25rem;
                            `}
                        >
                            <span
                                css={css`
                                    width: 0.5rem;
                                    height: 0.5rem;
                                    background-color: ${th.colors.tertiary};
                                    border-radius: 50%;
                                    display: inline-block;
                                    animation: pulse 1.5s infinite ease-in-out;
                                    animation-delay: 0s;

                                    @keyframes pulse {
                                        0%,
                                        100% {
                                            opacity: 0.4;
                                        }
                                        50% {
                                            opacity: 1;
                                        }
                                    }
                                `}
                            />
                            <span
                                css={css`
                                    width: 0.5rem;
                                    height: 0.5rem;
                                    background-color: ${th.colors.tertiary};
                                    border-radius: 50%;
                                    display: inline-block;
                                    animation: pulse 1.5s infinite ease-in-out;
                                    animation-delay: 0.2s;
                                `}
                            />
                            <span
                                css={css`
                                    width: 0.5rem;
                                    height: 0.5rem;
                                    background-color: ${th.colors.tertiary};
                                    border-radius: 50%;
                                    display: inline-block;
                                    animation: pulse 1.5s infinite ease-in-out;
                                    animation-delay: 0.4s;
                                `}
                            />
                        </div>
                    </View>
                )}

                <div id="messages-end" ref={messagesEndRef} />
            </div>
        )
    }
)

export default ChatMessages
